<template>
  <main>
    <contact-us-blurb />
    <div
      v-if="$route.query.share"
      class="container-fluid bg-white pt-4"
    >
      <div class="container">
        <share-portal-notification
          :name="patient"
          :isvalid="isvalid"
        />
      </div>
    </div>
    <div class="container-fluid bg-white pt-4">
      <div class="container">
        <food-navbar
          :source="$route.query.share ? 'share_portal' : 'tracker'"
          current="meal-entries"
        />
      </div>
    </div>
    <div
      v-if="isvalid"
      class="container-fluid bg-white py-5"
    >
      <div class="container">
        <div class="page-sidebar-layout">
          <div class="side main">
            <div class="page-title-box">
              <span>Meal Entries for </span>
              <date-picker
                v-model="selectedDate"
                :hidenav="hidenav"
                @input="update_date"
              />
            </div>

            <div
              v-if="is_free_user"
              class="upgrade-blurb"
            >
              <h6>Unlock with Premium</h6>
              <p>You've found a feature that's only available to Premium users.  The information below is sample data.  You're on the Free plan, which gives you all the basic benefits of FFF without the bells and whistles.  Upgrade to the Premium plan today to unlock more.</p>
              <div class="button-container">
                <a
                  href="/account/subscriptions"
                  class="button"
                >Yes Please!</a>
              </div>
            </div>

            <div class="meal-entries-grid">
              <div
                v-if="nomealitems"
                style="padding:20px; text-align:center;"
              >
                <p>There are no meal entries for this day.</p>
                <p>
                  <router-link
                    class="button mt-5 p-3 px-5"
                    :to="{ name: 'my-favorites' }"
                  >
                    Add a Food
                  </router-link>
                </p>
              </div>
              <div
                v-for="group in meal_groups"
                class="meal-group"
              >
                <div class="group-icon">
                  <i
                    v-if="group.name == 'Breakfast'"
                    class="fas fa-coffee"
                  />
                  <i
                    v-if="group.name == 'Lunch'"
                    class="fas fa-carrot"
                  />
                  <i
                    v-if="group.name == 'Dinner'"
                    class="fas fa-utensils"
                  />
                  <i
                    v-if="group.name == 'Snack'"
                    class="fas fa-apple-alt"
                  />
                </div>
                <h2>{{ group.name }}</h2>
                <div class="entry-timeline">
                  <div class="line" />
                  <div class="entries">
                    <div
                      v-for="entry in group.entries"
                      class="entry"
                      :class="[entry.color]"
                    >
                      <div class="icon" />
                      <div class="text">
                        <strong>{{ entry.name }} <a
                          v-if="usertype != 'Guest'"
                          href="#"
                          @click.prevent="delete_meal_entry(entry.meal_log_id)"
                        >(Delete)</a></strong>
                        <span class="separator">&bull;</span>
                        <span>{{ entry.id }}</span>
                        <span class="separator">&bull;</span>
                        <span>{{ entry.factor }}</span>
                        <span class="separator">&bull;</span>
                        <span>{{ entry.serving_size }}</span>
                        <span class="separator">&bull;</span>
                        <span>{{ entry.food_group }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="group-line" />
                <div class="summary-split">
                  <div class="side total">
                    <div class="total-row main">
                      <div class="circle">
                        <span>{{ round_half_down(group.total_food_factors) }}</span>
                      </div>
                      <div
                        v-tooltip:top="'Your Daily Food Factors'"
                        class="title"
                      >
                        Total Food Factors
                      </div>
                    </div>
                    <div class="total-row green">
                      <div class="circle">
                        <span>{{ round_half_down(group.chart_data[0]) }}</span>
                      </div>
                      <div class="title">
                        Green Foods
                      </div>
                    </div>
                    <div class="total-row yellow">
                      <div class="circle">
                        <span>{{ round_half_down(group.chart_data[1]) }}</span>
                      </div>
                      <div class="title">
                        Yellow Foods
                      </div>
                    </div>
                    <div class="total-row red">
                      <div class="circle">
                        <span>{{ round_half_down(group.chart_data[2]) }}</span>
                      </div>
                      <div class="title">
                        Red Foods
                      </div>
                    </div>
                  </div>
                  <div class="side chart">
                    <h3 class="text-center">
                      Meal Colors
                    </h3>
                    <div class="chart-container">
                      <meal-entry-chart :chart_data="group.chart_graphdata" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="side sidebar">
            <factor-scale
              :factor="scorecard.total_food_factor"
              :individual_factor="scorecard.individual_factor"
            />

            <div
              v-if="!is_free_user && usertype != 'Guest'"
              class="button-list"
            >
              <router-link
                :to="{name: 'food-search'}"
                class="item item-primary"
              >
                <span class="label">Add a Food</span>
                <span class="icon"><i class="fas fa-chevron-right" /></span>
              </router-link>
              <router-link
                :to="{name: 'recipe-maker'}"
                class="item"
              >
                <span class="label">Add/Create a Recipe</span>
                <span class="icon"><i class="fas fa-chevron-right" /></span>
              </router-link>
              <router-link
                :to="{name: 'my-favorites'}"
                class="item"
              >
                <span class="label">Add a Favorite</span>
                <span class="icon"><i class="fas fa-chevron-right" /></span>
              </router-link>
            </div>

            <form
              v-if="!is_free_user && usertype !== 'Guest' && user_in_spirals(['carb-counting', 'diabetes', 'renal-diabetes'])"
              class="sidebar-form"
              autocomplete="off"
              @submit.prevent="submit_blood_sugar"
            >
              <div class="title">
                Add Blood Sugar Entry
              </div>
              <div class="field horizontal">
                <div><label for="bs_meal">For:</label></div>
                <div>
                  <div class="select-container">
                    <select
                      id="bs_meal"
                      v-model="blood_sugar_data.meal"
                      class="fff-ui"
                    >
                      <option value="">
                        Choose a Meal
                      </option>
                      <option value="breakfast - before">
                        Before Breakfast
                      </option>
                      <option value="breakfast - after">
                        After Breakfast
                      </option>
                      <option value="lunch - before">
                        Before Lunch
                      </option>
                      <option value="lunch - after">
                        After Lunch
                      </option>
                      <option value="dinner - before">
                        Before Dinner
                      </option>
                      <option value="dinner - after">
                        After Dinner
                      </option>
                      <option value="bedtime">
                        At Bedtime
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field horizontal">
                <div><label for="bs_value">Value:</label></div>
                <div>
                  <input
                    id="bs_value"
                    v-model="blood_sugar_data.value"
                    type="text"
                    class="fff-ui"
                  >
                </div>
              </div>
              <div class="field">
                <div class="button-container fluid">
                  <button
                    type="submit"
                    class="button fluid"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>

            <goal-scale :goaldata="goaldata" />
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import MealEntryChart from '../components/MealEntryChart.js'
import { intersection, map } from 'lodash';
import utils, {round_half_down, round_quarter} from "../utils";
import DatePicker from "../components/DatePicker";

export default {
  name: 'MealEntries',
  components: {
    DatePicker,
    MealEntryChart
  },
  data() {
    return {
      intersection,
      map,
      usertype:'',
      usersub:'',
      isvalid: true,
      patient: '',
      hidenav: 'hide',
      date_type:'day',
      blood_sugar_data: {
        meal: '',
        value: '0.00',
      },
      nomealitems: false,
      meal_groups: [
        {
          name: 'Breakfast',
          entries: [
            {name: 'Food #1 Name', id: 'ID 1', color: 'green', factor: '1G', serving_size: '1 cup', food_group: 'Food Group'},
          ],
          chart_data: [4, 3, 2], // Green, Yellow, Red
          chart_graphdata:[40,30,20],
          total_food_factors: 6,
        },
        {
          name: 'Lunch',
          entries: [
            {name: 'Food #1 Name', id: 'ID 1', color: 'green', factor: '1G', serving_size: '1 cup', food_group: 'Food Group'},
          ],
          chart_data: [1, 2, 3], // Green, Yellow, Red
          chart_graphdata:[40,30,20],
          total_food_factors: 5,
        },
        {
          name: 'Dinner',
          entries: [
            {name: 'Food #1 Name', id: 'ID 1', color: 'green', factor: '1G', serving_size: '1 cup', food_group: 'Food Group'},
          ],
          chart_data: [4, 2, 2], // Green, Yellow, Red
          chart_graphdata:[40,30,20],
          total_food_factors: 8,
        },
        {
          name: 'Snack',
          entries: [
            {name: 'Food #1 Name', id: 'ID 1', color: 'green', factor: '1G', serving_size: '1 cup', food_group: 'Food Group'},
          ],
          chart_data: [1, 1, 1], // Green, Yellow, Red
          chart_graphdata:[40,30,20],
          total_food_factors: 3,
        },
      ],
      goaldata:{},
      scorecard:{
        individual_factor:22,
        total_food_factor:5
      }
    }
  },
  computed: {
    is_free_user: function () {
      if ( this.usertype == 'Guest' && this.usersub == 'individual' ) { return false; }
      return !this.$parent.user || this.$parent.user.type != 'individual';
    },
    user_food_spirals: function () {
      return map(this.$parent.user['food-spiral'], 'id');
    },
    selectedDate: {
      get: function () {
        return this.$store.state.selection.dateOnFoodTrackerScreen;
      },
      set: function (newValue) {
        this.$store.dispatch('selection/setDateOnFoodTrackerScreen', newValue);
      },
    },
  },
  mounted(){
    this.check_user();
    this.load_data();
  },
  methods: {
    round_quarter,
    round_half_down,
    user_in_spirals: function (spirals) {
      // Return whether or not the user is in any of the given FOOD SPIRALs
      return intersection(this.user_food_spirals, spirals).length >= 1;
    },

    async check_user(){
      await this.$store.dispatch('loading/increment');

      let checkuser = await this.$http('/api/share-portal/usertype',{
        method:"get",
      }).then(response => response.json())
        .then(async function(json) {
          if(json.message == "Unauthenticated."){
            throw new Error(JSON.stringify(json));
          }else{
            return json;
          }
        });

      await this.$store.dispatch('loading/decrement');

      this.usersub = checkuser.subscription;

      if(checkuser.type=="Guest"){
        this.usertype = "Guest";
      }
      if(checkuser.type=="User"){
        this.usertype = "User";
      }
    },
    async submit_blood_sugar() {
      if(this.blood_sugar_data.meal == ''){
        alert("Please select a meal option for the blood sugar entry.");
      }
      else if(parseFloat(this.blood_sugar_data.value)<0.01){
        alert("Please enter a number greater than zero for the blood sugar entry.");
      }else{
        let addbs = await this.$http('/api/bloodsugar/add',{
          method:"post",
          body:JSON.stringify({
            'timezone':Intl.DateTimeFormat().resolvedOptions().timeZone,
            'date':this.selectedDate,
            'value':this.blood_sugar_data.value,
            'meal':this.blood_sugar_data.meal
          })
        }).then(response => response.json())
          .then(async function(json) {
            return json;
          });
        alert("Blood Sugar entry added for "+this.selectedDate);
        this.blood_sugar_data.meal='';
        this.blood_sugar_data.value='0.00';
        console.log(addbs);
      }
    },
    update_date(date) {
      document.querySelector(".page-sidebar-layout").focus();
      this.load_data();
    },
    async load_data(){
      if (this.is_free_user) {
        await this.check_user(); // try again after loading this
      }
      if (this.is_free_user) {
        // Dummy data
        var mealdata = await JSON.parse('{"breakfast":{"items":[{"meal_log_id":300,"food_items_sid":"14715f3238da0cb56535","food_item_spirals_sid":"14735f32396c60a89474","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"1","serving_size_unit":"floz","meal":"breakfast","type":"food","type_of_food":"food","rm_g":"30.0000","rm_g_ingredient_ratio":"1.0582","factor_ingredient_consumed":"1.0582","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"115.5000","total_fluids_consumed":"0.0000","color":"Y","food_groups":["Breads & Starches"],"allergens":["soy","milk","wheat"],"items":[{"food_items_sid":"14715f3238da0cb56535","name":"TOASTER PASTRIES,FRUIT,FROSTED","ss_id":18938,"food_item_spirals_sid":"14735f32396c60a89474","spiral_name":"ccfs","color":"Y","rec_serving_size":"1 oz.","rm_serving_size":"28.35","factor":"1","cal_serving":"109.1475","carb_serving":"20.363805","fiber_serving":"0.5103","fat_serving":"2.55717","na_serving":"88.1685","k_serving":"24.381","p_serving":"20.1285","fluids":"0","6_major_categories":"BREAD & STARCHES","usda":"y","usda_color":"Y","usda_factor":1,"usda_serving_size":"1 OZ.","usda_fluids":"0"}]},{"meal_log_id":301,"food_items_sid":"14715f32389000732807","food_item_spirals_sid":"14735f323925d4e66773","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"0.5","serving_size_unit":"cp","meal":"breakfast","type":"food","type_of_food":"food","rm_g":"120.0000","rm_g_ingredient_ratio":"0.5000","factor_ingredient_consumed":"0.5000","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"73.2000","total_fluids_consumed":"60.0000","color":"R","food_groups":["Milk & Dairy"],"allergens":["milk"],"items":[{"food_items_sid":"14715f32389000732807","name":"WHOLE MILK","ss_id":1077,"food_item_spirals_sid":"14735f323925d4e66773","spiral_name":"ccfs","color":"R","rec_serving_size":"1 c.","rm_serving_size":"240","factor":"1","cal_serving":"146.4","carb_serving":"5.76","fiber_serving":"0","fat_serving":"7.8","na_serving":"51.6","k_serving":"158.4","p_serving":"100.8","fluids":"120","6_major_categories":"MILK & DAIRY","usda":"y","usda_color":"R","usda_factor":1,"usda_serving_size":"1 C.","usda_fluids":"120"}]}],"total_food_factors":2,"green_food_factors":0,"yellow_food_factors":1,"red_food_factors":1,"green_food_percentage":0,"yellow_food_percentage":20,"red_food_percentage":80},"lunch":{"items":[{"meal_log_id":295,"food_items_sid":"14715f3238afc92df429","food_item_spirals_sid":"14735f323945e3355696","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"1","serving_size_unit":"cp","meal":"lunch","type":"food","type_of_food":"food","rm_g":"240.0000","rm_g_ingredient_ratio":"2.0000","factor_ingredient_consumed":"2.0000","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"343.2000","total_fluids_consumed":"0.0000","color":"R","food_groups":["Breads & Starches"],"allergens":["milk","egg"],"items":[{"food_items_sid":"14715f3238afc92df429","name":"POTATO SALAD","ss_id":11414,"food_item_spirals_sid":"14735f323945e3355696","spiral_name":"ccfs","color":"R","rec_serving_size":"1\/2 c.","rm_serving_size":"120","factor":"1","cal_serving":"171.6","carb_serving":"13.404","fiber_serving":"1.56","fat_serving":"9.84","na_serving":"634.8","k_serving":"304.8","p_serving":"62.4","fluids":"0","6_major_categories":"BREAD & STARCHES","usda":"y","usda_color":"R","usda_factor":1,"usda_serving_size":"1\/2 C.","usda_fluids":"0"}]}],"total_food_factors":2,"green_food_factors":0,"yellow_food_factors":0,"red_food_factors":2,"green_food_percentage":0,"yellow_food_percentage":0,"red_food_percentage":100},"dinner":{"items":[{"meal_log_id":296,"food_items_sid":"14715f32389657668530","food_item_spirals_sid":"14735f32392c0b79c459","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"4","serving_size_unit":"floz","meal":"dinner","type":"food","type_of_food":"food","rm_g":"120.0000","rm_g_ingredient_ratio":"2.1239","factor_ingredient_consumed":"0.0000","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"265.2000","total_fluids_consumed":"0.0000","color":"G","food_groups":["Meats & Protein"],"allergens":[],"items":[{"food_items_sid":"14715f32389657668530","name":"CHICKEN THIGH, RAW","ss_id":5091,"food_item_spirals_sid":"14735f32392c0b79c459","spiral_name":"ccfs","color":"G","rec_serving_size":"2 oz.","rm_serving_size":"56.5","factor":"0","cal_serving":"124.865","carb_serving":"0.14175","fiber_serving":"0","fat_serving":"9.38465","na_serving":"45.927","k_serving":"115.668","p_serving":"89.019","fluids":"0","6_major_categories":"MEATS","usda":"y","usda_color":"G","usda_factor":0,"usda_serving_size":"2 OZ.","usda_fluids":"0"}]},{"meal_log_id":299,"food_items_sid":"14715f3238ad2bca2220","food_item_spirals_sid":"14735f323943ebac7940","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"0.5","serving_size_unit":"cp","meal":"dinner","type":"food","type_of_food":"food","rm_g":"120.0000","rm_g_ingredient_ratio":"1.0000","factor_ingredient_consumed":"1.0000","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"42.0000","total_fluids_consumed":"0.0000","color":"G","food_groups":["Vegetables"],"allergens":[],"items":[{"food_items_sid":"14715f3238ad2bca2220","name":"BROCCOLI, COOKED","ss_id":11091,"food_item_spirals_sid":"14735f323943ebac7940","spiral_name":"ccfs","color":"G","rec_serving_size":"1\/2 c.","rm_serving_size":"120","factor":"0","cal_serving":"42","carb_serving":"8.616","fiber_serving":"3.96","fat_serving":"0.492","na_serving":"49.2","k_serving":"351.6","p_serving":"80.4","fluids":"0","6_major_categories":"VEGETABLES","usda":"y","usda_color":"G","usda_factor":1,"usda_serving_size":"1\/2 C.","usda_fluids":"0"}]}],"total_food_factors":1,"green_food_factors":1,"yellow_food_factors":0,"red_food_factors":0,"green_food_percentage":100,"yellow_food_percentage":0,"red_food_percentage":0},"snack":{"items":[{"meal_log_id":298,"food_items_sid":"14715f3238a6d34cf784","food_item_spirals_sid":"14735f323940034cd691","food_recipe_id":null,"fast_food_items_sid":null,"fast_food_item_spirals_sid":null,"date":"2021-02-09 00:00:00","serving_size_value":"1","serving_size_unit":"cp","meal":"snack","type":"food","type_of_food":"food","rm_g":"240.0000","rm_g_ingredient_ratio":"1.7143","factor_ingredient_consumed":"1.7143","rm_g_recipe_ratio":null,"factor_recipe_consumed":null,"total_calories_consumed":"97.7143","total_fluids_consumed":"0.0000","color":"G","food_groups":["Fruits"],"allergens":[],"items":[{"food_items_sid":"14715f3238a6d34cf784","name":"BLUEBERRIES,RAW","ss_id":9050,"food_item_spirals_sid":"14735f323940034cd691","spiral_name":"ccfs","color":"G","rec_serving_size":"1 c.","rm_serving_size":"140","factor":"1","cal_serving":"57","carb_serving":"10.4328","fiber_serving":"2.4","fat_serving":"0.33","na_serving":"0.72","k_serving":"55.44","p_serving":"8.64","fluids":"0","6_major_categories":"FRUITS","usda":"y","usda_color":"G","usda_factor":1,"usda_serving_size":"1 C.","usda_fluids":"0"}]}],"total_food_factors":2,"green_food_factors":2,"yellow_food_factors":0,"red_food_factors":0,"green_food_percentage":100,"yellow_food_percentage":0,"red_food_percentage":0},"meta":{"score_card":{"total_food_factor":7,"individual_factor":10},"goals":{"total_assesments":86,"weight":{"start_weight":"185","weight":"185","goal_weight":"lose","target_weight":"15","perweek_weight":".5","goal":170,"goal_weight_weeks":"0\/30","goal_weight_change":55},"exercise":{"date_goal":"2021-02-09","steps_current":"1000","steps_goals":"3000","weekly_frequency":"2-3","duration":"100","type":"light"},"fluids":{"current":260,"goal":"1420"},"sleep":{"days_6_to_8":"3","goal":7},"calories":{"current":689,"goal":1550}},"patient":"AI User 2 !sc!"}}');
      } else {
        await this.$store.dispatch('loading/increment');

        var mealdata = await this.$http('/api/meals/mealentries',{
          method:"post",
          body:JSON.stringify({
            'timezone':Intl.DateTimeFormat().resolvedOptions().timeZone,
            'date':this.selectedDate
          })
        }).then(response => response.json())
          .then(async function(json) {
            if(json.message == "Unauthenticated."){
              throw new Error(JSON.stringify(json));
            }else{
              return json;
            }
          });

        await this.$store.dispatch('loading/decrement');
      }

      if(mealdata.message == "Invalid."){
        this.isvalid=false;
        return false;
      }

      console.log("mealdata");
      console.log(mealdata);
      this.patient  = mealdata.meta.patient;
      this.goaldata = mealdata.meta.goals;
      this.scorecard.individual_factor = mealdata.meta.score_card.individual_factor;
      this.scorecard.total_food_factor = mealdata.meta.score_card.total_food_factor;

      this.meal_groups=[];
      this.nomealitems=true;
      if(mealdata.breakfast){
        this.nomealitems=false;
        let breakfastentries = await this.process_items_for_display(mealdata.breakfast.items);
        let breakfastchart = [mealdata.breakfast.green_food_factors,mealdata.breakfast.yellow_food_factors,mealdata.breakfast.red_food_factors];
        let breadfastchartgraph = [mealdata.breakfast.green_food_percentage,mealdata.breakfast.yellow_food_percentage,mealdata.breakfast.red_food_percentage];
        this.meal_groups.push({"name":'Breakfast','entries':breakfastentries,'chart_data':breakfastchart,'chart_graphdata':breadfastchartgraph,'total_food_factors':utils.round_factor(mealdata.breakfast.total_food_factors)});
      }
      if(mealdata.lunch){
        this.nomealitems=false;
        let lunchentries = await this.process_items_for_display(mealdata.lunch.items);
        let lunchchart = [mealdata.lunch.green_food_factors,mealdata.lunch.yellow_food_factors,mealdata.lunch.red_food_factors];
        let lunchchartgraph = [mealdata.lunch.green_food_percentage,mealdata.lunch.yellow_food_percentage,mealdata.lunch.red_food_percentage];
        this.meal_groups.push({"name":'Lunch','entries':lunchentries,'chart_data':lunchchart,'chart_graphdata':lunchchartgraph,'total_food_factors':utils.round_factor(mealdata.lunch.total_food_factors)});
      }
      if(mealdata.dinner){
        this.nomealitems=false;
        let dinnerentries = await this.process_items_for_display(mealdata.dinner.items);
        let dinnerchart = [mealdata.dinner.green_food_factors,mealdata.dinner.yellow_food_factors,mealdata.dinner.red_food_factors];
        let dinnerchartgraph = [mealdata.dinner.green_food_percentage,mealdata.dinner.yellow_food_percentage,mealdata.dinner.red_food_percentage];
        this.meal_groups.push({"name":'Dinner','entries':dinnerentries,'chart_data':dinnerchart,'chart_graphdata':dinnerchartgraph,'total_food_factors':utils.round_factor(mealdata.dinner.total_food_factors)});
      }
      if(mealdata.snack){
        this.nomealitems=false;
        let snackentries = await this.process_items_for_display(mealdata.snack.items);
        let snackchart = [mealdata.snack.green_food_factors,mealdata.snack.yellow_food_factors,mealdata.snack.red_food_factors];
        let snackchartgraph = [mealdata.snack.green_food_percentage,mealdata.snack.yellow_food_percentage,mealdata.snack.red_food_percentage];
        this.meal_groups.push({"name":'Snack','entries':snackentries,'chart_data':snackchart,'chart_graphdata':snackchartgraph,'total_food_factors':utils.round_factor(mealdata.snack.total_food_factors)});
      }

    },
    process_items_for_display(items){
      let entries = [];
      if(items.length>0){
        for(let i=0; i<items.length; i++){ /*for both recipes and food items have items as a sub array*/
          let itparent = items[i];
          if(itparent.type == 'food') {
            if(items[i].items.length>0){
              for(let j =0; j<items[i].items.length; j++){
                let it = items[i].items[j];
                console.log(it);
                let factor = utils.round_quarter(itparent.factor_ingredient_consumed)+itparent.color;
                let food_group = itparent.food_groups.join(', ');
                let servingsize = this.entry_unit_cleanup((itparent.serving_size_value * 1) + ' ' + itparent.serving_size_unit);
                entries.push({meal_log_id:items[i].meal_log_id,name: it.name, id: 'ID ' + it.ss_id, color: (itparent.color=='R'?'red':(itparent.color=='G'?'green':'yellow')), factor: factor, serving_size: servingsize, food_group: food_group});
              }
            }
          } else { /* Recipe */
            if(items[i].recipe.length>0){
              for(let j =0; j<items[i].recipe.length; j++){
                let it = items[i].recipe[j];
                //console.log(it);
                let factor = utils.round_quarter(itparent.factor_recipe_consumed)+itparent.color;
                let food_group = itparent.food_groups.join(', ');
                let servingsize = this.entry_unit_cleanup((itparent.serving_size_value * 1) + ' ' + itparent.serving_size_unit);
                entries.push({meal_log_id:items[i].meal_log_id,name: it.name, id: 'Recipe', color: (it.rm_color=='R'?'red':(it.rm_color=='G'?'green':'yellow')), factor: factor, serving_size: servingsize, food_group: food_group});
              }
            }
          }
        }
      }
      return entries;
    },
    entry_unit_cleanup(serving_size){
      let rs = serving_size.split(" ");
      let units = {
        'tsp.':'teaspoons',
        'tsp':'teaspoons',
        'c.':'cups',
        'c':'cups',
        'T.':'tablespoons',
        'T':'tablespoons',
        't.':'teaspoons',
        't':'teaspoons',
        'oz.':'ounces',
        'oz':'ounces',
        'floz':'ounces',
        'lb': 'pounds',
        'g':'grams',
        'cp':'cups',
        'tablespoon':'tablespoons',
        'pt':'pints',
        'qt':'quarts',
        'gal':'gallons',
        'teaspoon':'teaspoons'
      };
      return rs[0]+' '+units[rs[1]];
    },
    async delete_meal_entry(meal_log_id){
      let mealdata = await this.$http('/api/meals/mealentries',{
        method:"delete",
        body:JSON.stringify({
          'meal_log_id':meal_log_id
        })
      }).then(response => response.json())
        .then(async function(json) {
          if(json.message == "Unauthenticated."){
            throw new Error(JSON.stringify(json));
          }else{
            alert("Food Meal Item Removed");
            return json;
          }
        });
      this.load_data();
    }
  },
};
</script>

<style lang="scss" scoped>

</style>
